import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import { fetchWrapper } from "utils/apiFetch";

// ─────────────────────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────────────────────

const API_URL    = "https://pro.mokshiq.in/retailer_action";  // adjust to your Django endpoint
                      // in a real app, read from URL params

const ROLE_CONFIG = {
  "White label":        { label: "White Label",        bg: "#EEEDFE", color: "#3C3489" },
  "Master Distributor": { label: "Master Distributor", bg: "#E6F1FB", color: "#0C447C" },
  "Distributer":        { label: "Distributor",        bg: "#E1F5EE", color: "#085041" },
  "Retailer":           { label: "Retailer",           bg: "#F1EFE8", color: "#444441" },
};

// ─────────────────────────────────────────────────────────────────────────────
// API helper — sends a POST with FormData to the Django endpoint
// ─────────────────────────────────────────────────────────────────────────────

async function apiPost(params) {
  const fd = new FormData();
  Object.entries(params).forEach(([k, v]) => fd.append(k, v));

  const res = await fetchWrapper(API_URL, {
    method: "POST",
   
    body: fd,
  });
  return res;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

// ─────────────────────────────────────────────────────────────────────────────
// Small UI components
// ─────────────────────────────────────────────────────────────────────────────

function RoleBadge({ role, small }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG["Retailer"];
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: small ? "2px 8px" : "3px 10px",
      borderRadius: 6, fontSize: small ? 11 : 12,
      fontWeight: 500, whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function Avatar({ name, size = 48 }) {
  const initials = name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#E1F5EE", color: "#085041",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 600, fontSize: size * 0.33, flexShrink: 0, letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

function InfoRow({ icon, label, value, valueStyle }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "0.5px solid #e5e7eb" }}>
      <span style={{ fontSize: 15, color: "#9ca3af", marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 500, ...valueStyle }}>{value}</div>
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: "#fff", border: "0.5px solid #e5e7eb",
      borderRadius: 12, padding: "1rem 1.25rem", ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: "0.85rem" }}>
      <span style={{ fontSize: 16, color: "#6b7280" }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{title}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
      <div style={{
        width: 32, height: 32, border: "3px solid #e5e7eb",
        borderTopColor: "#185FA5", borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = type === "success"
    ? { bg: "#E1F5EE", border: "#9FE1CB", text: "#085041" }
    : { bg: "#FCEBEB", border: "#F7C1C1", text: "#791F1F" };

  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 2000,
      background: colors.bg, border: `1px solid ${colors.border}`,
      color: colors.text, borderRadius: 10, padding: "10px 18px",
      fontSize: 13, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      {type === "success" ? "✅" : "❌"} {message}
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: colors.text, lineHeight: 1 }}>×</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ParentChainCard
// ─────────────────────────────────────────────────────────────────────────────

function ParentChainCard({ role, name, phone, isLast }) {
  if (!name) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
      <div style={{
        background: "#f9fafb", border: "0.5px solid #e5e7eb",
        borderRadius: 10, padding: "10px 14px", flex: 1, minWidth: 0,
      }}>
        <RoleBadge role={role} small />
        <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>📞 {phone}</div>
      </div>
      {!isLast && <div style={{ padding: "0 6px", color: "#d1d5db", fontSize: 18, flexShrink: 0 }}>›</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ChildTable
// ─────────────────────────────────────────────────────────────────────────────

function ChildTable({ members, type, onView, onEdit, onDelete }) {
  if (!members || members.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "#9ca3af", padding: "1rem 0", textAlign: "center" }}>
        No {type} found
      </div>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 13 }}>
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: "22%" }} />
          <col />
          <col style={{ width: 110 }} />
          <col style={{ width: 100 }} />
          <col style={{ width: 80 }} />
        </colgroup>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            {["ID", "Name", "Email", "Phone", "Wallet", "Actions"].map(h => (
              <th key={h} style={{ padding: "7px 10px", fontSize: 11, fontWeight: 600, color: "#6b7280", textAlign: "left", borderBottom: "0.5px solid #e5e7eb" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr key={m.id} style={{ borderBottom: "0.5px solid #f3f4f6" }}>
              <td style={{ padding: "7px 10px", color: "#9ca3af" }}>{m.id}</td>
              <td style={{ padding: "7px 10px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</td>
              <td style={{ padding: "7px 10px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.Email}</td>
              <td style={{ padding: "7px 10px" }}>{m.Phone}</td>
              <td style={{ padding: "7px 10px", fontWeight: 500, color: m.wallet_amount > 0 ? "#0F6E56" : m.wallet_amount < 0 ? "#A32D2D" : "#9ca3af" }}>
                ₹{(m.wallet_amount || 0).toFixed(2)}
              </td>
              <td style={{ padding: "7px 10px" }}>
                <div style={{ display: "flex", gap: 4 }}>
                  {[
                    { ic: "👁", title: "View",   fn: () => onView(m) },
                    
                  ].map(({ ic, title, fn }) => (
                    <button key={title} title={title} onClick={fn}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "2px 4px", borderRadius: 4 }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EditModal
// ─────────────────────────────────────────────────────────────────────────────

function EditModal({ member, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name:          member.name,
    Email:         member.Email,
    Phone:         member.Phone,
    Role:          member.Role,
    wallet_amount: member.wallet_amount,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name  = "Name is required";
    if (!form.Email.trim()) e.Email = "Email is required";
    if (!form.Phone.trim()) e.Phone = "Phone is required";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...member, ...form });
  };

  const field = (label, key, type = "text", required = true) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
        {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
      </label>
      <input type={type} value={form[key]}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: null })); }}
        style={{
          height: 36, padding: "0 10px",
          border: `1px solid ${errors[key] ? "#ef4444" : "#d1d5db"}`,
          borderRadius: 8, fontSize: 13, outline: "none",
          background: "#fff", color: "#111827",
        }}
      />
      {errors[key] && <span style={{ fontSize: 11, color: "#ef4444" }}>{errors[key]}</span>}
    </div>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={member.name} size={36} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Edit member</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>ID: {member.id} · <RoleBadge role={member.Role} small /></div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9ca3af", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
          {field("Full name", "name")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            {field("Email", "Email", "email")}
            {field("Phone", "Phone")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Role <span style={{ color: "#ef4444" }}>*</span></label>
              <select value={form.Role} onChange={e => setForm(f => ({ ...f, Role: e.target.value }))}
                style={{ height: 36, padding: "0 8px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "#fff", color: "#111827" }}>
                {Object.keys(ROLE_CONFIG).map(r => <option key={r} value={r}>{ROLE_CONFIG[r].label}</option>)}
              </select>
            </div>
            {field("Wallet amount", "wallet_amount", "number", false)}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "0.875rem 1.25rem", borderTop: "0.5px solid #e5e7eb", background: "#f9fafb" }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "7px 18px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: saving ? "#93c5fd" : "#185FA5", color: "#fff", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontWeight: 500 }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ServiceModal — assign/unassign services
// ─────────────────────────────────────────────────────────────────────────────

function ServiceModal({ memberId, memberName, onClose, showToast }) {
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving,  setSaving]      = useState(false);

  useEffect(() => {
    apiPost({ action: "GETServiceList", ret_idd: memberId })
      .then(r => setServices(r.data || []))
      .catch(() => showToast("Failed to load services", "error"))
      .finally(() => setLoading(false));
  }, [memberId]);

  const toggle = id => setServices(prev =>
    prev.map(s => s.id === id ? { ...s, status: !s.status } : s)
  );

  const handleSave = async () => {
    setSaving(true);
    const selected = services.filter(s => s.status).map(s => ({ id: s.id }));
    try {
      const r = await apiPost({ action: "AssignServiceList", id: memberId, service_list: JSON.stringify(selected) });
      showToast(r.data, r.status ? "success" : "error");
      if (r.status) onClose();
    } catch {
      showToast("Failed to assign services", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Manage services — {memberName}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9ca3af" }}>×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "1rem 1.25rem", flex: 1 }}>
          {loading ? <Spinner /> : services.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "2rem 0" }}>No services available</div>
          ) : services.map(s => (
            <div key={s.id} onClick={() => toggle(s.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 6,
                background: s.status ? "#E1F5EE" : "#f9fafb",
                border: `0.5px solid ${s.status ? "#9FE1CB" : "#e5e7eb"}`,
              }}>
              {s.photo && <img src={s.photo} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                {s.category && <div style={{ fontSize: 11, color: "#9ca3af" }}>{s.category}</div>}
              </div>
              <div style={{
                width: 18, height: 18, borderRadius: 4,
                border: `2px solid ${s.status ? "#0F6E56" : "#d1d5db"}`,
                background: s.status ? "#0F6E56" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {s.status && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "0.875rem 1.25rem", borderTop: "0.5px solid #e5e7eb", background: "#f9fafb" }}>
          <button onClick={onClose} style={{ padding: "7px 18px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: saving ? "#93c5fd" : "#185FA5", color: "#fff", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontWeight: 500 }}>
            {saving ? "Saving…" : "Save services"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function MemberDetailPage() {
  const [member,       setMember]       = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [editOpen,     setEditOpen]     = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);  // null = editing main member; otherwise a child member obj
  const [savingEdit,   setSavingEdit]   = useState(false);
  const [serviceOpen,  setServiceOpen]  = useState(false);
  const [activeTab,    setActiveTab]    = useState(null);
  const [toast,        setToast]        = useState(null);  // { message, type }
const { id } = useParams();
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);
  const navigate = useNavigate();

  // ── fetch member on mount ──────────────────────────────────────────────────
  const fetchMember = useCallback(async () => {
    
    setLoading(true);
    setError(null);
    try {
      const r = await apiPost({ action: "GETBYID", id: id });
      if (!r.status) throw new Error(r.data || "Failed to load member");
      setMember(r.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMember(); }, [fetchMember]);

  // ── edit save ──────────────────────────────────────────────────────────────
  const handleSave = async (updated) => {
    setSavingEdit(true);
    try {
      const r = await apiPost({
        action: "ADDUpdate",
        id:     updated.id,
        name:   updated.name,
        email:  updated.Email,
        phone:  updated.Phone,
      });
      if (r.status) {
        // If editing the main member, refresh the whole page data
        if (!editTarget || editTarget.id === member.id) {
          await fetchMember();
        } else {
          // For a child member, patch it locally to avoid a full reload
          setMember(prev => {
            const children = { ...prev.children };
            for (const key of Object.keys(children)) {
              children[key] = children[key].map(m =>
                m.id === updated.id ? { ...m, name: updated.name, Email: updated.Email, Phone: updated.Phone } : m
              );
            }
            return { ...prev, children };
          });
        }
        showToast(r.data, "success");
        setEditOpen(false);
        setEditTarget(null);
      } else {
        showToast(r.data, "error");
      }
    } catch {
      showToast("Failed to update member", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (targetMember) => {
    if (!window.confirm(`Delete "${targetMember.name}"? This cannot be undone.`)) return;
    try {
      const r = await apiPost({ action: "DELETE", id: targetMember.id });
      if (r.status) {
        showToast(r.data, "success");
        if (targetMember.id === member.id) {
          window.history.back();
        } else {
          await fetchMember();
        }
      } else {
        showToast(r.data, "error");
      }
    } catch {
      showToast("Failed to delete member", "error");
    }
  };

   const handleView = (report) => {
    console.log("Viewing report:", report);
    // Navigate to detail page or open modal
    window.location.replace(`/member_setting_management?userid=${report}`);
  };
  const openEditMain  = () => { setEditTarget(null);   setEditOpen(true); };
  const openEditChild = (m) => { setEditTarget(m);     setEditOpen(true); };
  const openViewChild = (m) => {  window.location.replace(`/member_setting_management?userid=${m.id}`); };

  // ── derived data ───────────────────────────────────────────────────────────
  const childTabs = member?.children
    ? [
        member.children.master_distributors?.length && { key: "master_distributors", label: "Master Distributors", data: member.children.master_distributors },
        member.children.distributors?.length        && { key: "distributors",        label: "Distributors",        data: member.children.distributors        },
        member.children.retailers?.length           && { key: "retailers",           label: "Retailers",           data: member.children.retailers           },
      ].filter(Boolean)
    : [];

  const currentTab = activeTab || childTabs[0]?.key || null;
  const walletColor = !member ? "#6b7280" : member.wallet_amount > 0 ? "#0F6E56" : member.wallet_amount < 0 ? "#A32D2D" : "#6b7280";

  const editingMember = editTarget || member;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
     <div className="card-body">
        <div class="container-fluid" style={{marginTop:"40px"}}>
             <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#f3f4f6", minHeight: "100vh", padding: ".25rem" }}>

      {/* Modals */}
      {editOpen && editingMember && (
        <EditModal
          member={editingMember}
          onClose={() => { setEditOpen(false); setEditTarget(null); }}
          onSave={handleSave}
          saving={savingEdit}
        />
      )}
      {serviceOpen && member && (
        <ServiceModal
          memberId={member.id}
          memberName={member.name}
          onClose={() => setServiceOpen(false)}
          showToast={showToast}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <button style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#6b7280", padding: 0 }}
            onClick={() => navigate('/member_management')}>
            ← Member management
          </button>

          
        </div>

        {/* Loading / error / content */}
        {loading ? (
          <Card><Spinner /></Card>
        ) : error ? (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem 0", color: "#A32D2D" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{error}</div>
              <button onClick={fetchMember} style={{ padding: "7px 18px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>
                Retry
              </button>
            </div>
          </Card>
        ) : member ? (
          <>
            {/* Profile card */}
            <Card>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                <Avatar name={member.name} size={56} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{member.name}</span>
                    <RoleBadge role={member.Role} />
                    <span style={{ fontSize: 12, background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 6 }}>ID: {member.id}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.5rem 1.5rem", marginTop: 12 }}>
                    <InfoRow icon="✉️" label="Email"          value={member.Email}                                       valueStyle={{ fontSize: 12, color: "#185FA5" }} />
                    <InfoRow icon="📞" label="Phone"          value={member.Phone} />
                    <InfoRow icon="💰" label="Wallet balance" value={`₹${member.wallet_amount.toFixed(2)}`}              valueStyle={{ color: walletColor, fontSize: 14 }} />
                  </div>
                </div>
              </div>
            </Card>

            {/* Parent hierarchy */}
            {member.parent_details && (
              <Card>
                <SectionTitle icon="🔗" title="Parent hierarchy" />
                <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap", overflowX: "auto" }}>
                  {member.parent_details.white_label && (
                    <ParentChainCard role="White label"
                      name={member.parent_details.white_label.name}
                      phone={member.parent_details.white_label.phone}
                      isLast={!member.parent_details.master_distributor && !member.parent_details.distributor}
                    />
                  )}
                  {member.parent_details.master_distributor && (
                    <ParentChainCard role="Master Distributor"
                      name={member.parent_details.master_distributor.name}
                      phone={member.parent_details.master_distributor.phone}
                      isLast={!member.parent_details.distributor}
                    />
                  )}
                  {member.parent_details.distributor && (
                    <ParentChainCard role="Distributer"
                      name={member.parent_details.distributor.name}
                      phone={member.parent_details.distributor.phone}
                      isLast
                    />
                  )}
                </div>
              </Card>
            )}

            {/* Children */}
            <Card>
              <SectionTitle icon="👥" title="Children members" />
              {childTabs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2rem 0", color: "#9ca3af", fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
                  No children — Retailers have no downstream members.
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 6, marginBottom: "1rem", flexWrap: "wrap" }}>
                    {childTabs.map(t => (
                      <button key={t.key} onClick={() => setActiveTab(t.key)}
                        style={{
                          padding: "5px 14px", borderRadius: 8, border: "1px solid",
                          borderColor: currentTab === t.key ? "#185FA5" : "#e5e7eb",
                          background:  currentTab === t.key ? "#E6F1FB" : "#fff",
                          color:       currentTab === t.key ? "#0C447C" : "#6b7280",
                          fontSize: 13, fontWeight: currentTab === t.key ? 600 : 400, cursor: "pointer",
                        }}>
                        {t.label}
                        <span style={{ marginLeft: 6, background: currentTab === t.key ? "#185FA5" : "#f3f4f6", color: currentTab === t.key ? "#fff" : "#6b7280", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                          {t.data.length}
                        </span>
                      </button>
                    ))}
                  </div>
                  {childTabs.map(t => (
                    <div key={t.key} style={{ display: currentTab === t.key ? "block" : "none" }}>
                      <ChildTable
                        members={t.data}
                        type={t.label}
                        
                        onDelete={handleDelete}
                      />
                    </div>
                  ))}
                </>
              )}
            </Card>

            {/* Quick actions */}
            <Card style={{ marginBottom: "1rem" }}>
              <SectionTitle icon="⚡" title="Quick actions" />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  
                  { label: "Add balance",     icon: "➕",  action: () => handleView(id) },
                  { label: "Manage services", icon: "🛠",  action: () => handleView(id) },
                  { label: "Change role",     icon: "🔄",  action: () => handleView(id) },
                  { label: "Reassign parent", icon: "🔀",  action: () => handleView(id)},
                ].map(a => (
                  <button key={a.label} onClick={a.action}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
                    {a.icon} {a.label}
                  </button>
                ))}
              </div>
            </Card>
          </>
        ) : null}
      </div>
    </div>
        </div>
   
    </div>
  );
}